import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import {
  Bell, Search, Plus, LogOut, User, X, Megaphone, FileText, Mail,
  DollarSign, Zap, Globe, Target, AlertTriangle, CheckCircle, Info,
  AlertCircle, ChevronRight, Loader2,
} from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

// ── Module icon/color maps ──
const MODULE_ICONS: Record<string, any> = {
  campaigns: Megaphone, content: FileText, email: Mail, leads: Target,
  ads: DollarSign, seo: Search, automations: Zap, "email-lists": Mail,
};
const MODULE_COLORS: Record<string, string> = {
  campaigns: "text-blue-600 bg-blue-50", content: "text-green-600 bg-green-50",
  email: "text-purple-600 bg-purple-50", leads: "text-amber-600 bg-amber-50",
  ads: "text-red-500 bg-red-50", seo: "text-cyan-600 bg-cyan-50",
  automations: "text-yellow-600 bg-yellow-50", "email-lists": "text-purple-600 bg-purple-50",
};
const NOTIF_ICONS: Record<string, any> = {
  success: CheckCircle, warning: AlertTriangle, error: AlertCircle, info: Info,
};
const NOTIF_COLORS: Record<string, string> = {
  success: "text-green-500", warning: "text-amber-500", error: "text-red-500", info: "text-blue-500",
};

export default function Header() {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);

  // ── Search state ──
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // ── Notifications ──
  const { data: notifData, refetch: refetchNotifs } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<any>("/notifications"),
    refetchInterval: 60000,
  });

  const unreadCount = notifData?.unreadCount || 0;

  // ── Search with debounce ──
  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setSearchResults(null); setShowSearch(false); return; }
    setSearching(true);
    try {
      const data = await api.get<any>(`/search?q=${encodeURIComponent(q)}`);
      setSearchResults(data);
      setShowSearch(true);
    } catch { setSearchResults(null); }
    setSearching(false);
  }, []);

  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.length < 2) { setSearchResults(null); setShowSearch(false); return; }
    searchTimer.current = setTimeout(() => doSearch(value), 300);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (searchRef.current && !searchRef.current.contains(target)) {
        setShowSearch(false);
      }
      if (createRef.current && !createRef.current.contains(target)) {
        setShowCreate(false);
      }
      if (notifRef.current && !notifRef.current.contains(target)) {
        setShowNotifs(false);
      }
      if (profileRef.current && !profileRef.current.contains(target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const createActions = [
    { label: "New Campaign", icon: Megaphone, color: "text-blue-600", action: () => navigate("/campaigns/create") },
    { label: "New Content", icon: FileText, color: "text-green-600", action: () => navigate("/content/create") },
    { label: "New Email", icon: Mail, color: "text-purple-600", action: () => navigate("/email/create") },
    { label: "New Ad", icon: DollarSign, color: "text-red-500", action: () => navigate("/ads/create") },
    { label: "Social Post", icon: Globe, color: "text-pink-600", action: () => navigate("/social") },
  ];

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      {/* ── Global Search ── */}
      <div className="flex-1 max-w-lg" ref={searchRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchInput(e.target.value)}
            onFocus={() => { if (searchResults?.results?.length) setShowSearch(true); }}
            placeholder="Search campaigns, content, analytics..."
            className="w-full pl-10 pr-10 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-colors"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500 animate-spin" />}
          {searchQuery && !searching && (
            <button onClick={() => { setSearchQuery(""); setSearchResults(null); setShowSearch(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Search Results Dropdown */}
          {showSearch && searchResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-[420px] overflow-y-auto">
              {searchResults.results?.length > 0 ? (
                <>
                  <div className="px-4 py-2 border-b border-gray-100">
                    <span className="text-xs font-semibold text-gray-500">{searchResults.total} results for "{searchResults.query}"</span>
                  </div>
                  {searchResults.results.map((r: any) => {
                    const Icon = MODULE_ICONS[r.module] || Search;
                    const color = MODULE_COLORS[r.module] || "text-gray-600 bg-gray-50";
                    return (
                      <button key={r.id} onClick={() => { navigate(r.path); setShowSearch(false); setSearchQuery(""); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                          <p className="text-xs text-gray-400">{r.subtitle}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                          r.status === "active" || r.status === "published" ? "bg-green-50 text-green-600"
                          : r.status === "draft" ? "bg-gray-100 text-gray-600"
                          : "bg-blue-50 text-blue-600"
                        }`}>{r.status}</span>
                      </button>
                    );
                  })}
                </>
              ) : (
                <div className="px-4 py-8 text-center text-gray-400">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No results for "{searchResults.query}"</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="flex items-center gap-2 ml-4">
        {/* Quick Create */}
        <div className="relative" ref={createRef}>
          <button onClick={() => { setShowCreate(!showCreate); setShowNotifs(false); setShowMenu(false); }}
            className="btn-primary text-sm">
            <Plus className="w-4 h-4" /> Create
          </button>
          {showCreate && (
            <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
              {createActions.map((a) => (
                <button key={a.label} onClick={() => { a.action(); setShowCreate(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                  <a.icon className={`w-4 h-4 ${a.color}`} />
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notification Bell */}
        <div className="relative" ref={notifRef}>
          <button onClick={() => { setShowNotifs(!showNotifs); setShowCreate(false); setShowMenu(false); refetchNotifs(); }}
            className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-50">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {showNotifs && (
            <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-[480px] flex flex-col">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h4 className="font-semibold text-gray-900">Notifications</h4>
                {unreadCount > 0 && (
                  <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div className="overflow-y-auto divide-y divide-gray-50 flex-1">
                {notifData?.notifications?.length ? notifData.notifications.map((n: any) => {
                  const NIcon = NOTIF_ICONS[n.type] || Info;
                  const nColor = NOTIF_COLORS[n.type] || "text-gray-500";
                  return (
                    <button key={n.id} onClick={() => { navigate(n.path); setShowNotifs(false); }}
                      className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${!n.read ? "bg-purple-50/30" : ""}`}>
                      <NIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${nColor}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{n.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(n.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
                    </button>
                  );
                }) : (
                  <div className="px-4 py-8 text-center text-gray-400">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No notifications</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Profile */}
        <div className="relative" ref={profileRef}>
          <button onClick={() => { setShowMenu(!showMenu); setShowCreate(false); setShowNotifs(false); }}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-sm font-medium text-gray-700">{user?.name}</p>
              <p className="text-xs text-gray-400">{organization?.name}</p>
            </div>
          </button>
          {showMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-gray-400">{user?.email}</p>
                <span className="inline-block mt-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">{organization?.plan}</span>
              </div>
              <button onClick={() => { navigate("/settings"); setShowMenu(false); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <User className="w-4 h-4" /> Profile & Settings
              </button>
              <button onClick={() => { logout(); setShowMenu(false); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
